/* ══ F15 STEP 3.7 — THE WRITER'S PURE HALF, THE SERVER'S TWIN OF `CoachNextWeek.swift` ════════════════════════════════════
 * `plan` answers "what should next week say?"; this turns that answer into the coach's decisions (`review`), binds the
 * accepted ones onto NEXT week's freshly cloned rows (`bind`), and maps those onto the sets the clone holds (`persist`).
 * Pure: nothing here talks to Postgres (`nextWeekWriter.ts` is the I/O half, in the service's order).
 *
 * 🔴 THE SWIFT BINDS. Pinned by every case's and chain week's `writer` block in `progression-fixture.json` — the review, the
 * outcome the automatic path reaches, and the exact writes, deletions and moves (`NextWeekFixture.writerJSON`).
 * ⚠️ A REJECTION IS NOT STORED: a suggestion is computed on demand from the logs; accepting it writes the new numbers;
 * rejecting it writes nothing, so next week repeats (the Swift header's decision 1).
 * ⚠️ IDS ARE STRINGS, COMPARED AS GIVEN — the callers hand lower-case ids; a suggestion's `key` upper-cases the library id
 * (`unitKey`, the Swift `uuidString` spelling) on both sides of `bind`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

import { type Row, type SectionRole, type SetRow, isRampSet, unitKey, units } from "./progression.ts";
import { DELOAD_WEEK_SENTENCE, type Plan } from "./coachPlan.ts";
import type { LadderTarget } from "./coachLadder.ts";
import type { RowEdit } from "./coachTabs.ts";

/** `CoachNextWeek.Suggestion` — one change a coach is asked to take: its before, after and the rule's own sentence. */
export interface Suggestion {
  /** `unitKey` of the unit — the address `bind` finds the same prescription by in the clone. */
  key: string;
  name: string;
  sectionRole: SectionRole;
  before: SetRow[];
  after: SetRow[];
  reason: string;
  /** F15 step 3.4 — the ladder's next exercise when this suggestion is a move; null otherwise. */
  move: LadderTarget | null;
  /** F15 4.1b — the row's own fields a rule tab writes (tempo · note · each side · hidden); absent / null = none. */
  rowEdit?: RowEdit | null;
}

/** `CoachNextWeek.Withheld` — a refusal, shown even when the rule is switched off (it is not a suggestion). */
export interface Withheld { name: string; reason: string }

export type Decision = "accept" | "reject";

/** `CoachNextWeek.Review`. */
export interface Review {
  assignmentId: string;
  suggestions: Suggestion[];
  withheld: Withheld[];
  unchangedCount: number;
  switchedOffCount: number;
  frozenForWantOfALog: boolean;
  warnings: string[];
  isDeloadWeek: boolean;
  proposals: string[];
  pausedForPain: string[];
  ladderSaid: string[];
}

/** `Review.readingOrder` — the deload first, then the ladder, then the proposals, then the warnings. */
export function readingOrder(review: Review): string[] {
  return [...(review.isDeloadWeek ? [DELOAD_WEEK_SENTENCE] : []), ...review.ladderSaid, ...review.proposals,
          ...review.warnings];
}

/** `Review.needsADecision`. */
export const needsADecision = (review: Review): boolean => review.suggestions.length > 0;

/** `CoachNextWeek.isSwitchedOff(rule:)` — `rule["enabled"] == false`, and nothing else. */
export function isSwitchedOff(rule: unknown): boolean {
  return rule !== null && typeof rule === "object" && !Array.isArray(rule)
    && (rule as Record<string, unknown>)["enabled"] === false;
}

/** `CoachNextWeek.review(_:)`. */
export function review(plan: Plan): Review {
  const suggestions: Suggestion[] = [];
  const withheld: Withheld[] = [];
  let unchanged = 0;
  let switchedOff = 0;

  for (const unitPlan of plan.plans) {
    const unit = unitPlan.unit;
    const outcome = unitPlan.outcome;
    if (outcome.kind === "changed") {
      if (!plan.isDeloadWeek && isSwitchedOff(unit.rule)) {
        switchedOff += 1;
        continue;
      }
      const key = unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets);
      suggestions.push({
        key,
        name: unit.name,
        sectionRole: unit.sectionRole,
        before: unit.sets,
        after: outcome.sets,
        reason: outcome.reason,
        move: plan.moves.find((m) => m.unitKey === key)?.target ?? null,
        rowEdit: (plan.rowEdits ?? []).find((e) => e.unitKey === key)?.edit ?? null,
      });
    } else if (outcome.kind === "unchanged") {
      unchanged += 1;
    } else {
      withheld.push({ name: unit.name, reason: outcome.reason });
    }
  }

  /* ⚠️ A REFUSAL REACHES THIS TYPE TWICE (the unit's `refused` AND `plan.warnings`); removed by EXACT equality. */
  const withheldReasons = new Set(withheld.map((w) => w.reason));
  /* F15 step 3.4 — a ladder move FIRST; the rest keep their day order (a stable partition). */
  const ordered = [...suggestions.filter((s) => s.move !== null), ...suggestions.filter((s) => s.move === null)];
  return {
    assignmentId: plan.assignmentId,
    suggestions: ordered,
    withheld,
    unchangedCount: unchanged,
    switchedOffCount: switchedOff,
    frozenForWantOfALog: plan.frozenForWantOfALog,
    warnings: plan.warnings.filter((w) => !withheldReasons.has(w)),
    isDeloadWeek: plan.isDeloadWeek,
    proposals: plan.proposals,
    pausedForPain: plan.pausedForPain,
    ladderSaid: plan.ladderSaid,
  };
}

// ── Binding a review onto next week ────────────────────────────────────────────────────────────────────────────────────

/** `CoachNextWeek.RowWrite` — one row's full set list, renumbered from 0. */
export interface RowWrite {
  rowId: string;
  sets: SetRow[];
  /** F15 step 3.4 — the ladder's next exercise this row becomes; null otherwise. */
  move: LadderTarget | null;
  /** F15 4.1b — the row's own fields to write; absent / null = none. */
  rowEdit?: RowEdit | null;
}

export type Binding =
  | { kind: "bound"; writes: RowWrite[] }
  | { kind: "nothingAccepted" }
  | { kind: "refused"; reason: string }
  | { kind: "undecided"; names: string[] };

/** `CoachNextWeek.renumbered`. */
export function renumbered(sets: SetRow[]): SetRow[] {
  return sets.map((set, index) => ({
    position: index, reps: set.reps, weightKg: set.weightKg, timeSeconds: set.timeSeconds,
    restSeconds: set.restSeconds, setType: set.setType,
  }));
}

/** `CoachNextWeek.unsplit(_:over:)` — the flat prescription reshaped to last week's geometry; null when it cannot fill the
 *  rows exactly. ⚠️ AN EMPTY PRESCRIPTION IS REFUSED BEFORE THE SINGLE-ROW ARM (it would otherwise empty the exercise); a
 *  single-row unit may gain or lose sets, so the whole list goes to that one row. */
export function unsplit(flat: SetRow[], rows: Row[]): RowWrite[] | null {
  const capacity = rows.reduce((n, r) => n + r.sets.length, 0);
  if (flat.length === 0) return null;
  const only = rows[0];
  if (rows.length === 1 && only !== undefined) return [{ rowId: only.id, sets: renumbered(flat), move: null }];
  if (capacity !== flat.length) return null;
  const out: RowWrite[] = [];
  let cursor = 0;
  for (const row of rows) {
    const slice = flat.slice(cursor, cursor + row.sets.length);
    cursor += row.sets.length;
    out.push({ rowId: row.id, sets: renumbered(slice), move: null });
  }
  return out;
}

export type Readiness =
  | { kind: "ready"; accepted: Suggestion[] }
  | { kind: "nothingAccepted" }
  | { kind: "undecided"; names: string[] };

/** `CoachNextWeek.readiness(_:decisions:)` — no suggestions is "nothing accepted", never "ready with nothing". */
export function readiness(review: Review, decisions: Record<string, Decision>): Readiness {
  if (review.suggestions.length === 0) return { kind: "nothingAccepted" };
  const undecided = review.suggestions.filter((s) => decisions[s.key] === undefined);
  if (undecided.length > 0) return { kind: "undecided", names: undecided.map((s) => s.name) };
  const accepted = review.suggestions.filter((s) => decisions[s.key] === "accept");
  if (accepted.length === 0) return { kind: "nothingAccepted" };
  return { kind: "ready", accepted };
}

/** `CoachNextWeek.bind(_:decisions:onto:)`. */
export function bind(review: Review, decisions: Record<string, Decision>, next: Row[]): Binding {
  const ready = readiness(review, decisions);
  if (ready.kind === "nothingAccepted") return { kind: "nothingAccepted" };
  if (ready.kind === "undecided") return { kind: "undecided", names: ready.names };
  const accepted = ready.accepted;

  const unitsByKey = new Map<string, Row[]>();
  for (const unit of units(next)) {
    const key = unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets);
    const rowIds = new Set(unit.rowIds);
    unitsByKey.set(key, next.filter((r) => rowIds.has(r.id)).sort((a, b) => a.ordinal - b.ordinal));
  }

  const writes: RowWrite[] = [];
  for (const suggestion of accepted) {
    const rows = unitsByKey.get(suggestion.key);
    if (rows === undefined || rows.length === 0) {
      return { kind: "refused", reason: `${suggestion.name}: the week being written does not `
        + "contain the prescription this suggestion was made against. Either it has "
        + "already been progressed, or the week was edited after the review — in "
        + "both cases writing the new numbers somewhere else would be a guess, so "
        + "nothing is written." };
    }
    const bound = unsplit(suggestion.after, rows);
    if (bound === null) {
      if (suggestion.after.length === 0) {
        return { kind: "refused", reason: `${suggestion.name}: the suggestion is for ZERO sets, `
          + "which would leave the exercise in next week with nothing to perform. "
          + "An empty prescription is refused rather than written — deleting the "
          + "movement is a decision, not a progression." };
      }
      return { kind: "refused", reason: `${suggestion.name}: next week stores this prescription `
        + `across ${rows.length} rows holding `
        + `${rows.reduce((n, r) => n + r.sets.length, 0)} sets, and the suggestion has `
        + `${suggestion.after.length}. Splitting the difference is a decision about `
        + "the pairing, not arithmetic, so nothing is written." };
    }
    // F15 4.1b — a row edit rides on every row of its unit too (a split prescription is written alike).
    writes.push(...bound.map((w) => ({ ...w, move: suggestion.move, rowEdit: suggestion.rowEdit ?? null })));
  }
  return { kind: "bound", writes };
}

// ── Persisting a binding onto the clone's sets ─────────────────────────────────────────────────────────────────────────

/** `CoachNextWeek.ExistingSet` — a set the clone holds; `carriesUnmodelledColumns` = it carries `rounds` / `distance_m`. */
export interface ExistingSet { id: string; position: number; carriesUnmodelledColumns: boolean }

/** `CoachNextWeek.SetWrite` — `id` null = an INSERT (the set is new). */
export interface SetWrite { id: string | null; rowId: string; set: SetRow }

/** `CoachNextWeek.RowMove` — F15 step 3.4: the row becomes the ladder's next exercise, its typical range from the sets. */
export interface RowMove {
  rowId: string;
  exerciseId: string;
  repsMin: number | null;
  repsMax: number | null;
  holdSecondsMin: number | null;
  holdSecondsMax: number | null;
}

/** `CoachNextWeek.RowFieldWrite` — F15 4.1b: one row's own fields as written (null = left alone). */
export interface RowFieldWrite { rowId: string; edit: RowEdit }

/** `CoachNextWeek.Persistence` — `rowEdits` (4.1b) are issued after the sets and before the moves. */
export interface Persistence { writes: SetWrite[]; deletions: string[]; moves: RowMove[]; rowEdits: RowFieldWrite[] }

export type PersistenceOutcome =
  | { kind: "ready"; persistence: Persistence }
  | { kind: "refused"; reason: string };

const minOf = (xs: number[]): number | null => (xs.length === 0 ? null : Math.min(...xs));
const maxOf = (xs: number[]): number | null => (xs.length === 0 ? null : Math.max(...xs));

/** `CoachNextWeek.persist(_:onto:)` — each written set binds onto the clone's set at the same place (by position order);
 *  a gained set is an insert; a lost one a deletion. */
export function persist(rows: RowWrite[], existing: Record<string, ExistingSet[]>): PersistenceOutcome {
  if (rows.length === 0) {
    return { kind: "refused", reason: "There is nothing to write. An accepted suggestion always "
      + "produces at least one row, so an empty write list means the caller assembled "
      + "it rather than taking it from `bind` — and writing nothing quietly would "
      + "report success for work that never happened." };
  }
  const seenRows = new Set<string>();
  const writes: SetWrite[] = [];
  const deletions: string[] = [];
  const moves: RowMove[] = [];
  const rowEdits: RowFieldWrite[] = [];

  for (const row of rows) {
    if (seenRows.has(row.rowId)) {
      return { kind: "refused", reason: "Two accepted suggestions want to write the same exercise "
        + "row. One of them would silently overwrite the other, so nothing is written." };
    }
    seenRows.add(row.rowId);
    const held = Object.prototype.hasOwnProperty.call(existing, row.rowId) ? existing[row.rowId] : undefined;
    if (held === undefined || held.length === 0) {
      return { kind: "refused", reason: "The week being written does not hold the exercise row "
        + "these numbers were bound to, or holds it with no sets at all. Writing them "
        + "somewhere else would be a guess, so nothing is written." };
    }
    const inOrder = [...held].sort((a, b) => a.position - b.position);
    if (row.sets.length > inOrder.length && inOrder.some((s) => s.carriesUnmodelledColumns)) {
      return { kind: "refused", reason: "This prescription gains a set, and the exercise is "
        + "measured in rounds or distance — columns the progression rules do not "
        + "model. The new set would be written with those blank, so nothing is "
        + "written and the change is left to be made by hand." };
    }
    row.sets.forEach((set, index) => {
      writes.push({ id: index < inOrder.length ? inOrder[index]!.id : null, rowId: row.rowId, set });
    });
    if (row.sets.length < inOrder.length) deletions.push(...inOrder.slice(row.sets.length).map((s) => s.id));
    if (row.rowEdit !== undefined && row.rowEdit !== null) rowEdits.push({ rowId: row.rowId, edit: row.rowEdit });
    if (row.move !== null) {
      const working = row.sets.filter((s) => !isRampSet(s));
      const reps = working.map((s) => s.reps).filter((v): v is number => v !== null);
      const seconds = working.map((s) => s.timeSeconds).filter((v): v is number => v !== null);
      moves.push({ rowId: row.rowId, exerciseId: row.move.id, repsMin: minOf(reps), repsMax: maxOf(reps),
                   holdSecondsMin: minOf(seconds), holdSecondsMax: maxOf(seconds) });
    }
  }
  return { kind: "ready", persistence: { writes, deletions, moves, rowEdits } };
}

// ── The automatic path ─────────────────────────────────────────────────────────────────────────────────────────────────

/** `CoachNextWeek.automaticDecisions(_:)` — every suggestion accepted (his 09-10 word, behind the per-client toggle). */
export function automaticDecisions(review: Review): Record<string, Decision> {
  const decisions: Record<string, Decision> = {};
  for (const suggestion of review.suggestions) decisions[suggestion.key] = "accept";
  return decisions;
}

/** `CoachNextWeekService.explain(_:)` — the binding's own sentence, never a re-wording of it. */
export function explain(binding: Binding): string {
  switch (binding.kind) {
    case "refused": return binding.reason;
    case "undecided": return "Not every suggestion has been answered: " + binding.names.join(", ");
    case "nothingAccepted": return "Nothing was accepted.";
    case "bound": return "Nothing was wrong.";
  }
}
